import * as React from "react";
import Header from "../header/header";
import Footer from "../footer/footer";

interface SkeletonLoaderProps {
  type: "orders" | "listings" | "account" | "product";
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ type }) => {
  const renderContent = () => {
    switch (type) {
      case "product":
        return (
          <div className="flex flex-col md:flex-row py-6">
            {/* Left Column - Gallery, Specs, Map */}
            <div className="md:w-2/3 pr-4">
              {/* Gallery Skeleton */}
              <div className="w-full h-96 bg-gray-300 rounded-lg animate-pulse mb-6" />
              
              {/* Specs Skeleton */}
              <div className="mt-6 animate-pulse">
                <div className="h-8 bg-gray-300 w-40 rounded mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="p-4 border rounded-lg">
                      <div className="h-4 bg-gray-300 w-1/3 rounded mb-2" />
                      <div className="h-4 bg-gray-300 w-2/3 rounded" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Extra Gear Skeleton */}
              <div className="mt-6 animate-pulse">
                <div className="h-8 bg-gray-300 w-48 rounded mb-4" />
                <div className="space-y-2">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="h-4 bg-gray-300 w-3/4 rounded" />
                  ))}
                </div>
              </div>

              {/* Map Skeleton */}
              <div className="mt-6 animate-pulse">
                <div className="h-8 bg-gray-300 w-32 rounded mb-2" />
                <div className="w-full h-64 bg-gray-300 rounded-lg" />
              </div>
            </div>

            {/* Right Column - Drone Info and Owner Info */}
            <div className="md:w-1/3">
              {/* Drone Info Skeleton */}
              <div className="mb-6 animate-pulse">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="h-8 bg-gray-300 w-48 rounded mb-2" />
                    <div className="h-4 bg-gray-300 w-32 rounded" />
                  </div>
                  <div className="h-8 w-8 bg-gray-300 rounded" />
                </div>

                {/* Price */}
                <div className="mb-4">
                  <div className="h-10 bg-gray-300 w-24 rounded" />
                </div>

                {/* Calendar */}
                <div className="h-64 bg-gray-300 rounded-lg mb-4" />

                {/* Price Breakdown */}
                <div className="space-y-2 mb-4">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="h-4 bg-gray-300 w-full rounded" />
                  ))}
                </div>

                {/* Rent Button */}
                <div className="h-12 bg-gray-300 rounded-lg w-full mb-6" />

                {/* Description */}
                <div className="mb-6">
                  <div className="h-6 bg-gray-300 w-32 rounded mb-2" />
                  <div className="space-y-2">
                    {[1, 2, 3].map((item) => (
                      <div key={item} className="h-4 bg-gray-300 w-full rounded" />
                    ))}
                  </div>
                </div>

                {/* Availability */}
                <div>
                  <div className="h-6 bg-gray-300 w-32 rounded mb-2" />
                  <div className="space-y-2">
                    {[1, 2].map((item) => (
                      <div key={item} className="h-4 bg-gray-300 w-full rounded" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Owner Info Skeleton */}
              <div className="bg-darkBlue rounded-lg p-6 animate-pulse">
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 bg-gray-300 rounded-full mb-4" />
                  <div className="h-4 bg-gray-300 w-32 rounded mb-2" />
                  <div className="h-3 bg-gray-300 w-24 rounded mb-1" />
                  <div className="h-3 bg-gray-300 w-20 rounded" />
                </div>
              </div>
            </div>
          </div>
        );

      case "account":
        return (
          <>
            <h1 className="text-3xl font-bold mb-6">
              <div className="h-8 bg-gray-300 w-40 rounded animate-pulse" />
            </h1>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-1/3 space-y-4">
                {/* Profile Card Skeleton */}
                <div className="bg-darkBlue rounded-lg p-6 animate-pulse">
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 bg-gray-300 rounded-full mb-4" />
                    <div className="h-4 bg-gray-300 w-32 rounded mb-2" />
                    <div className="h-3 bg-gray-300 w-24 rounded mb-1" />
                    <div className="h-3 bg-gray-300 w-20 rounded" />
                  </div>
                </div>

                {/* Action Buttons Skeleton */}
                <div className="h-10 bg-gray-300 w-full rounded animate-pulse" />
                <div className="h-10 bg-gray-300 w-full rounded animate-pulse" />

                {/* Message Card Skeleton */}
                <div className="bg-bluePrimary rounded-lg p-6 animate-pulse">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 bg-gray-300 rounded-full mb-4" />
                    <div className="h-4 bg-gray-300 w-40 rounded" />
                  </div>
                </div>
              </div>

              {/* Details Cards Skeleton */}
              <div className="md:w-2/3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((item) => (
                    <div
                      key={item}
                      className="bg-white rounded-lg p-6 shadow-sm animate-pulse"
                    >
                      <div className="space-y-3">
                        <div className="h-5 bg-gray-300 w-1/3 rounded" />
                        <div className="h-4 bg-gray-300 w-2/3 rounded" />
                        <div className="h-4 bg-gray-300 w-1/2 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        );

      default:
        return (
          <div className="flex flex-col md:flex-row gap-6 py-20">
            {/* Left column with profile and message card */}
            <div className="md:w-1/3 space-y-4">
              {/* Profile Card Skeleton */}
              <div className="bg-darkBlue rounded-lg p-6 animate-pulse">
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 bg-gray-300 rounded-full mb-4" />
                  <div className="h-4 bg-gray-300 w-32 rounded mb-2" />
                  <div className="h-3 bg-gray-300 w-24 rounded mb-1" />
                  <div className="h-3 bg-gray-300 w-20 rounded" />
                </div>
              </div>
              
              {/* Message Card Skeleton */}
              <div className="bg-bluePrimary rounded-lg p-6 animate-pulse">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full mb-4" />
                  <div className="h-4 bg-gray-300 w-40 rounded" />
                </div>
              </div>
            </div>

            {/* Right column with content */}
            <div className="md:w-2/3">
              <div className="flex justify-between items-center mb-6">
                <div className="h-8 bg-gray-300 w-32 rounded animate-pulse" />
                {type === "listings" && (
                  <div className="h-10 bg-gray-300 w-32 rounded animate-pulse" />
                )}
              </div>

              {/* Content Items */}
              <div className="space-y-4">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="border rounded-lg p-4 animate-pulse"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-2 flex-1">
                        <div className="h-5 bg-gray-300 w-3/4 rounded" />
                        <div className="h-4 bg-gray-300 w-1/2 rounded" />
                        <div className="h-4 bg-gray-300 w-1/4 rounded" />
                      </div>
                      <div className="w-24 h-24 bg-gray-300 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow w-full flex justify-center">
        <div className="w-full max-w-7xl p-4">
          <Header />
          <div className="py-20">
            {renderContent()}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SkeletonLoader; 